import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

// Cookie helpers for iOS standalone web app persistence
const setCookie = (name, value, days) => {
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax; Secure`;
};

const getCookie = (name) => {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? decodeURIComponent(match[2]) : null;
};

const deleteCookie = (name) => {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
};

// Save token to both localStorage and cookie (belt + suspenders)
const saveToken = (token) => {
  try { localStorage.setItem('token', token); } catch (e) { /* storage full or blocked */ }
  setCookie('crm_token', token, 30); // 30 days
};

// Get token — try localStorage first, fallback to cookie
const getToken = () => {
  let token = null;
  try { token = localStorage.getItem('token'); } catch (e) { /* blocked */ }
  if (!token) {
    token = getCookie('crm_token');
    // Sync back to localStorage if found in cookie
    if (token) {
      try { localStorage.setItem('token', token); } catch (e) { /* */ }
    }
  }
  return token;
};

// Remove token from everywhere
const removeToken = () => {
  try { localStorage.removeItem('token'); } catch (e) { /* */ }
  deleteCookie('crm_token');
};

// Global axios interceptor — attach token to ALL requests automatically
axios.interceptors.request.use((config) => {
  const token = getToken();
  if (token && config.url?.startsWith(API_URL)) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Global response interceptor — handle 401 (expired/invalid token)
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && !error.config.url?.includes('/login')) {
      // Token is invalid/expired — clear it
      removeToken();
      // Redirect to login if not already there
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Check if the user is authenticated
export const isAuthenticated = async () => {
  const token = getToken();
  if (!token) return false;

  try {
    const response = await axios.get(`${API_URL}/users/profile`);
    return response.status === 200;
  } catch (err) {
    // Only a real auth rejection (401/403) means the token is invalid.
    // A network error (offline, server unreachable) must NOT log the user
    // out — keep them authenticated so the PWA works offline with cached data.
    if (err.response && (err.response.status === 401 || err.response.status === 403)) {
      return false;
    }
    console.warn("Profile check failed (network/offline) — keeping session:", err?.message);
    return true;
  }
};

// Login User
export const loginUser = async (credentials) => {
  const response = await axios.post(`${API_URL}/users/login`, credentials);
  const { token } = response.data;
  saveToken(token);
  return response.data;
};

export const logoutUser = () => {
  removeToken();
};

// Get the current user's profile
export const getUserProfile = async (userId) => {
  const response = await axios.get(`${API_URL}/users/profile/${userId}`);
  return response.data;
};

export const getProfile = async () => {
  try {
    const response = await axios.get(`${API_URL}/users/profile`);
    // Cache the profile so the app can render (and stay logged in) offline.
    try { localStorage.setItem('profile', JSON.stringify(response.data)); } catch (e) { /* storage full/blocked */ }
    return response.data;
  } catch (err) {
    // Offline / server unreachable: fall back to the last cached profile.
    if (!err.response) {
      try {
        const cached = localStorage.getItem('profile');
        if (cached) return JSON.parse(cached);
      } catch (e) { /* ignore */ }
    }
    throw err;
  }
};

// Update the current user's profile
export const updateUserProfile = async (userId, profileData) => {
  const response = await axios.put(`${API_URL}/users/update/`, profileData);
  return response.data;
};

export const requestPasswordReset = async (email) => {
  const response = await axios.post(`${API_URL}/users/request-reset`, { email });
  return response.data;
};

export const changePassword = async (token, newPassword) => {
  if (token) {
    await axios.post(`${API_URL}/users/reset-password`, { token, newPassword });
  } else {
    const response = await axios.put(`${API_URL}/users/change-password`, { newPassword });
    return response.data;
  }
};

export const verifyCurrentPassword = async (currentPassword) => {
  const response = await axios.post(`${API_URL}/users/verify-password`, { currentPassword });
  return response.data.isValid;
};

export const createUser = async (userData) => {
  const response = await axios.post(`${API_URL}/users/create`, userData);
  return response.data;
};
